import { useState, useEffect } from "react";
import axios from "axios";
import SearchableSelectMenu from "../SelectMenus/SearchableSelectMenu";
import { useForm } from "react-hook-form";
import React from "react";
import { useConfiguration } from "@/app/hooks/useConfiguration";
import { PRIMARY_BUTTON, SECONDARY_BUTTON } from "@/constants";
import { ArrowUpTrayIcon, CheckCircleIcon, DocumentIcon } from "@heroicons/react/24/outline";

interface ImageToUrlGenerationProps {
  onImageUpload?: (url: string) => void;
  resetTrigger?: number;
}

const PathList = [
  { title: "admin-tool", id: "1" },
  { title: "app_page", id: "2" },
  { title: "Cat Pages", id: "3" },
  { title: "Clinic list", id: "4" },
  { title: "clinics", id: "5" },
  { title: "doctor", id: "6" },
  { title: "Food-Accessories", id: "7" },
  { title: "grooming_page", id: "8" },
  { title: "home_page", id: "9" },
  { title: "landing page", id: "10" },
  { title: "Marketing_banner_webp", id: "11" },
  { title: "Nearme", id: "12" },
  { title: "Website-Astro/about_us", id: "13" },
  { title: "Website-Astro/Banner Image", id: "14" },
  { title: "Website-Astro/clinic_details_page", id: "15" },
  { title: "Website-Astro/clinic_page", id: "16" },
  { title: "Website-Astro/Component Images", id: "17" },
  { title: "Website-Astro/consultation_page", id: "18" },
  { title: "Website-Astro/diagnostic_page", id: "19" },
  { title: "Website-Astro/doctors_pages", id: "20" },
  { title: "Website-Astro/emergency_page", id: "21" },
  { title: "Website-Astro/fonts", id: "22" },
  { title: "Website-Astro/Footer", id: "23" },
  { title: "Website-Astro/grooming_page", id: "24" },
  { title: "Website-Astro/home_page_image", id: "25" },
  { title: "Website-Astro/hospital_pages", id: "26" },
  { title: "Website-Astro/icons", id: "27" },
  { title: "Website-Astro/our_services", id: "28" },
  { title: "Website-Astro/surgery_page", id: "29" },
  { title: "Website-Astro/vaccination_page", id: "30" },
  { title: "Website-Astro/video_and_gif", id: "31" },
];

const ImageToUrlGeneration: React.FC<ImageToUrlGenerationProps> = ({ onImageUpload, resetTrigger }) => {
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const configuration = useConfiguration();

  const formHook = useForm<any>({
    mode: "onChange",
    defaultValues: {},
  });

  useEffect(() => {
    setSelectedImage(null);
    setImageUrl(null);
    setLoading(false);
    setIsLocked(false);
    formHook.reset({});
  }, [resetTrigger]);

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedImage(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedImage) {
      alert("Please select an image first.");
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedImage);
    formData.append("path", formHook.getValues("path"));

    try {
      setLoading(true);
      const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);
      const response = await axios.post(
        "https://qc.catalog.vetic.in/s3/upload/", // Replace with your actual upload endpoint
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `${accessToken}`,
          },
        },
      );

      if (response.data && response.data.link) {
        setIsLocked(true);
        if (onImageUpload) {
          onImageUpload(response.data.link);
        } else {
          setImageUrl(response.data.link); // Use `link` property from API response
        }
      } else {
        alert("Failed to retrieve image URL.");
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      alert("Error uploading image. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (imageUrl) {
      navigator.clipboard.writeText(imageUrl);
    }
  };

  return (
    <div className="">
      <div className="flex justify-center py-5 px-4">
        <div className="bg-white p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-sm w-full max-w-lg">
          <h1 className="text-lg font-semibold text-slate-900 text-center">Image Uploader</h1>
          <p className="mt-1 mb-6 text-xs text-slate-500 text-center">
            Upload an image and get a shareable URL.
          </p>

          <label className="block mb-1.5 text-xs font-medium text-slate-600">Image file</label>
          <label
            className={`mb-4 flex flex-col items-center justify-center gap-2 w-full rounded-xl border border-dashed px-4 py-6 text-center transition-colors ${
              loading || isLocked
                ? "border-slate-200 bg-slate-50 cursor-not-allowed opacity-60"
                : "border-slate-300 bg-slate-50/60 hover:border-slate-400 hover:bg-slate-100 cursor-pointer"
            }`}
          >
            <ArrowUpTrayIcon className="h-6 w-6 text-slate-400" />
            <span className="text-xs font-medium text-slate-700">Click to select an image</span>
            <span className="text-[11px] text-slate-400">PNG, JPG, WEBP, GIF</span>
            <input
              type="file"
              accept=""
              onChange={handleImageChange}
              className="hidden"
              disabled={loading || isLocked}
            />
          </label>

          {selectedImage && (
            <div className="mb-4 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
              <DocumentIcon className="h-4 w-4 shrink-0 text-slate-400" />
              <span className="min-w-0 flex-1 truncate text-xs text-slate-700" title={selectedImage.name}>
                {selectedImage.name}
              </span>
              <span className="shrink-0 text-[11px] text-slate-400">
                {(selectedImage.size / 1024).toFixed(0)} KB
              </span>
            </div>
          )}
          <label className="block mb-1.5 text-xs font-medium text-slate-600">Target path</label>
          <div className="pb-4">
            <SearchableSelectMenu
              errorMessages={[{ message: "Path is required", type: "required" }]}
              onSelectItem={(item) => {
                if (item) {
                  formHook.setValue(`path`, item.title);
                }
              }}
              fieldError={formHook?.formState?.errors?.path}
              register={formHook.register(`path`, { required: true })}
              selectItems={PathList}
              placeholder="Select Targeted Path To Upload"
              showTooltips={true}
              showTypedErrors={false}
              showDropdownIcon={true}
              defaultSelected={PathList?.filter((oc) => oc.title === formHook.watch(`path`))[0]}
              listBoxClassName="w-full"
              className="text-gray-800 w-20"
              containerClassName="w-full"
              disabled={loading || isLocked}
            />
          </div>
          <button
            onClick={handleUpload}
            className={`${PRIMARY_BUTTON} w-full disabled:opacity-50 disabled:cursor-not-allowed`}
            disabled={loading || isLocked}
          >
            {loading ? "Uploading..." : "Upload"}
          </button>

          {imageUrl && !onImageUpload && (
            <div className="mt-6 rounded-xl border border-green-200 bg-green-50 p-4">
              <p className="flex items-center gap-1.5 text-xs font-medium text-green-700">
                <CheckCircleIcon className="h-4 w-4" />
                Upload successful
              </p>
              <div className="mt-3 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={imageUrl}
                  className="min-w-0 flex-1 rounded-lg border border-green-200 bg-white px-3 h-9 text-xs text-slate-700 focus:outline-none"
                />
                <button onClick={copyToClipboard} className={`${SECONDARY_BUTTON} shrink-0`}>
                  Copy URL
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {imageUrl && !onImageUpload && (
        <div className="flex justify-center px-4 pb-8">
          <div className="w-full max-w-lg overflow-hidden rounded-2xl border border-slate-200 bg-white p-2">
            <img src={imageUrl} alt={imageUrl} className="mx-auto max-h-[300px] w-auto object-contain" />
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageToUrlGeneration;
